/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";
import { useShop } from "@app/vue/providers/ShopConnection";

type TabItem = {
  to: string;
  icon: string;
  label: string;
  badge?: number;
  /** Path used for the active check when `to` carries a query string. */
  activePath?: string;
};

export const MobileTabBar = defineComponent({
  name: "MobileTabBar",
  setup() {
    const auth = useAuth();
    const route = useRoute();
    const shop = useShop();
    const { t } = useLocalization();

    const isManager = computed(() => auth.user.value?.role === "manager");
    const tabs = computed<TabItem[]>(() => [
      {
        to: "/app/pos",
        icon: "point_of_sale",
        label: t("nav.core.pos"),
      },
      {
        to: "/app/tables",
        icon: "table_restaurant",
        label: t("nav.core.tables"),
      },
      ...(isManager.value
        ? [
            {
              to: "/app/reports",
              icon: "monitoring",
              label: t("nav.core.reports"),
            },
          ]
        : [
            {
              to: "/app/reports/transactions?responsive=true",
              activePath: "/app/reports/transactions",
              icon: "history",
              label: t("reports.nav.payments"),
            },
          ]),
      {
        to: "/app/manager/reservations",
        icon: "calendar_month",
        label: t("nav.labels.reservations"),
        badge: shop.pendingReservationRequestsCount.value,
      },
      ...(isManager.value
        ? [
            //            {
            //              to: "/app/settings",
            //              icon: "settings",
            //              label: t("nav.labels.settings"),
            //            },
          ]
        : []),
    ]);

    // Signed-out visitors (landing, login, signup) get their own short tab set.
    const guestTabs = computed<TabItem[]>(() => [
      { to: "/", icon: "home", label: t("nav.labels.home") },
      { to: "/login", icon: "login", label: t("nav.quick_links.login") },
      // /signup works everywhere; it is only advertised on staging.
      ...(import.meta.env.VITE_ENV === "staging"
        ? [
            {
              to: "/signup",
              icon: "person_add",
              label: t("nav.quick_links.signup"),
            },
          ]
        : []),
    ]);

    const isActive = (to: string) =>
      to === "/" ? route.path === "/" : route.path.startsWith(to);

    const tabClasses = (active: boolean) => [
      "relative flex min-w-0 flex-1 select-none flex-col items-center justify-center gap-0.5 px-0.5 pb-2 pt-0 text-[10px] leading-none transition-colors",
      active ? "text-primary" : "text-base-content",
    ];

    return () => (
      <nav
        aria-label={t("nav.labels.mobile_navigation")}
        class="fixed inset-x-0 border-t bottom-0 h-[calc(3.75rem+env(safe-area-inset-bottom))] border-t bg-base-200 pb-[env(safe-area-inset-bottom)] shadow-lg backdrop-blur-md min-[699px]:hidden"
      >
        <div class="mx-auto flex h-[3.75rem] max-w-lg items-stretch">
          {(auth.user.value ? tabs.value : guestTabs.value).map((tab) => {
            const active = isActive(tab.activePath ?? tab.to);
            return (
              <RouterLink
                to={tab.to}
                class={tabClasses(active)}
                aria-current={active ? "page" : undefined}
              >
                <span class="relative leading-none">
                  <span class="material-symbols-outlined text-[23px] leading-none">
                    {tab.icon}
                  </span>
                  {tab.badge && tab.badge > 0 ? (
                    <span class="absolute -right-2.5 -top-1.5 flex min-w-[16px] items-center justify-center rounded-full bg-error px-1 text-[9px] font-bold leading-4 text-error-content">
                      {tab.badge > 99 ? "99+" : tab.badge}
                    </span>
                  ) : null}
                </span>
                <span class="w-full truncate px-0.5 text-center uppercase">
                  {tab.label}
                </span>
              </RouterLink>
            );
          })}

          {auth.user.value ? (
            <RouterLink
              to="/app/menu"
              class={tabClasses(isActive("/app/menu"))}
              aria-current={isActive("/app/menu") ? "page" : undefined}
            >
              <span class="material-symbols-outlined text-[23px] leading-none">
                menu
              </span>
              <span class="w-full truncate px-0.5 text-center uppercase">
                {t("nav.labels.menu")}
              </span>
            </RouterLink>
          ) : null}
        </div>
      </nav>
    );
  },
});
